import { useCallback } from 'react';
import { useCreateProject } from '@atlassian/jira-project-create-common/src/services/create-project/index.tsx';
import type {
	CreateProjectBody,
	CreateProjectResponse,
} from '@atlassian/jira-project-create-common/src/services/create-project/types.tsx';
import { PROJECT_CREATE_APP_NAME } from '@atlassian/jira-project-create-common/src/constants.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { getValidProjectName } from './utils.tsx';

type UseSubmitProjectCreateReturn = [
	(
		payload: CreateProjectBody,
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => Promise<CreateProjectResponse | undefined>,
	isLoading: boolean,
	hasError: boolean,
];

export const useSubmitProjectCreate = (
	onSuccess: (
		project: CreateProjectResponse,
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void,
	onFail: (error: Error) => void,
): UseSubmitProjectCreateReturn => {
	const [createProject, isLoading, hasError] = useCreateProject();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const submitProjectCreate = useCallback(
		async (
			body: CreateProjectBody,
			e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			try {
				const validName = await getValidProjectName(body.payload.name);
				const result = await createProject({
					...body,
					payload: { ...body.payload, name: validName },
				});

				fireTrackAnalytics(createAnalyticsEvent({}), 'submitProjectCreate success', {
					templateKey: body.payload.templateKey,
				});
				if (result !== undefined) {
					onSuccess(result, e, analyticsEvent);
				}
				return result;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: `${PROJECT_CREATE_APP_NAME}.use-create-project-with-shared-config.error`,
						packageName: 'crossFlowOnboardingModal',
						teamName: 'Galileo (Growth)',
					},
					error,
					attributes: {
						templateKey: body.payload.templateKey,
					},
				});

				onFail(error);
			}
		},
		[createAnalyticsEvent, createProject, onFail, onSuccess],
	);

	return [submitProjectCreate, isLoading, hasError];
};
